import { inject, Injectable } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Video } from '@shared/models/video/video.model';
import type { SharingDialogData } from './sharing-dialog/sharing-dialog';
import { AnalyticsEvents, VideoCut } from '@shared/models';
import { from, Observable, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from '@core/services';

@Injectable({
  providedIn: 'root',
})
export class SharingService {
  private readonly dialog = inject(Dialog);
  private readonly analytics = inject(AnalyticsService);
  readonly sharedIdEndpoint = `${environment.apiUrl}/v1/shared-id`;

  /* Apart from the start bundle, with its QR code library, and fetched once the application is up. */
  private readonly sharingDialog = () =>
    import('./sharing-dialog/sharing-dialog').then((m) => m.SharingDialog);

  constructor() {
    setTimeout(() => void this.sharingDialog());
  }

  async shareVideo(video: Video): Promise<DialogRef<void>> {
    this.analytics.trackEvent(AnalyticsEvents.CaseShared, {
      case_id: video.id,
      case_status: video.publicationStatus,
    });
    return this.dialog.open<void, SharingDialogData>(await this.sharingDialog(), {
      ariaLabel: 'Share the case',
      data: {
        title: video.title,
        type: 'case',
        link: this.generateSharingCaseLink([video]),
        canEditSharedId: true,
        videoId: video.id,
      },
    });
  }

  shareVideoCut(cut: VideoCut): Observable<void> {
    this.analytics.trackEvent(AnalyticsEvents.VideoEditShared, {
      case_id: cut.videoId,
      video_edit_id: cut.cutId,
    });
    return from(this.sharingDialog()).pipe(
      switchMap(
        (dialog) =>
          this.dialog.open<void, SharingDialogData>(dialog, {
            ariaLabel: 'Share the video edit',
            data: {
              title: cut.title,
              type: 'cut',
              link: this.generateSharingCutLink(cut),
            },
          }).closed,
      ),
    );
  }

  generateSharingCaseLink(videos: Video[]) {
    const videoParams: string[] = videos.map((video) => `video=${video.sharedId}`);
    return `${location.origin}/shared?${videoParams.join('&')}`;
  }

  private generateSharingCutLink(videoCut: VideoCut) {
    return `${location.origin}/video-edit/${videoCut.cutId}`;
  }
}
